import type { IconNames } from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';

/**
 * Map each backend Notification `event` (see NOTIFICATION_EVENT_TYPES in the API)
 * to a tabler-icon name used by our <Icon /> component.
 *
 * Keep this file in sync with api/notification/constants.py.
 * If the backend introduces a new event without a mapping here the UI will fall
 * back to a generic icon (the calling component should handle the fallback).
 */
export interface NotificationEventIconMeta {
	icon: IconNames;
	color: ColorNames;
}

export const NOTIFICATION_EVENT_ICON_MAP: Record<
	string,
	NotificationEventIconMeta
> = {
	// Account management
	free_trial_ending: {
		icon: 'speakerphone',
		color: 'icon/primary/default',
	},
	// Announcement
	announcement: { icon: 'speakerphone', color: 'icon/primary/default' },
	// AI Task Run events
	ai_task_run_completed: { icon: 'sparkles', color: 'icon/primary/default' },
	ai_task_run_failed: { icon: 'sparkles', color: 'icon/primary/default' },
	ai_task_run_timeout: { icon: 'sparkles', color: 'icon/primary/default' },
	// Metadata push jobs
	metadata_push_job_completed: {
		icon: 'arrowBigUpLines',
		color: 'icon/primary/default',
	},
	metadata_push_job_failed: {
		icon: 'arrowBigUpLines',
		color: 'icon/primary/default',
	},
	// Extraction jobs
	extraction_job_completed: {
		icon: 'arrowBigDownLines',
		color: 'icon/primary/default',
	},
	extraction_job_failed: {
		icon: 'arrowBigDownLines',
		color: 'icon/primary/default',
	},
	github_change_request: {
		icon: 'arrowBigDownLines',
		color: 'icon/primary/default',
	},
	// Mentions
	mentions: { icon: 'at', color: 'icon/primary/default' },
	// Query block jobs
	queryblock_job_completed: { icon: 'code', color: 'icon/primary/default' },
	queryblock_job_failed: { icon: 'code', color: 'icon/primary/default' },
	// Comment events
	comment_new_thread: { icon: 'message', color: 'icon/primary/default' },
	comment_reply: { icon: 'arrowForward', color: 'icon/primary/default' },
	comment_mention: { icon: 'at', color: 'icon/primary/default' },
	comment_reaction: { icon: 'message', color: 'icon/primary/default' },
	comment_resolved: { icon: 'message', color: 'icon/primary/default' },
	// Question events
	question: {
		icon: 'messageCircleQuestion',
		color: 'icon/primary/default',
	},
	question_owner_assigned: {
		icon: 'messageCircleQuestion',
		color: 'icon/primary/default',
	},
	question_group_assigned: {
		icon: 'messageCircleQuestion',
		color: 'icon/primary/default',
	},
	question_answer: {
		icon: 'messageCircleQuestion',
		color: 'icon/primary/default',
	},
	question_new: {
		icon: 'messageCircleQuestion',
		color: 'icon/primary/default',
	},
	question_reply: { icon: 'arrowForward', color: 'icon/primary/default' },
	question_reply_edited: {
		icon: 'arrowForward',
		color: 'icon/primary/default',
	},
	question_reply_removed: {
		icon: 'arrowForward',
		color: 'icon/primary/default',
	},
	// Access/change requests
	access_request: { icon: 'edit', color: 'icon/primary/default' },
	applied_access_request: { icon: 'edit', color: 'icon/primary/default' },
	change_request: { icon: 'edit', color: 'icon/primary/default' },
	applied_change_request: { icon: 'edit', color: 'icon/primary/default' },
	// Resource events
	resource_archived: { icon: 'trash', color: 'icon/primary/default' },
	resource_collection_added: { icon: 'file', color: 'icon/primary/default' },
	resource_collection_removed: {
		icon: 'file',
		color: 'icon/primary/default',
	},
	resource_definition_updated: {
		icon: 'pencil',
		color: 'icon/primary/default',
	},
	resource_description_updated: {
		icon: 'file',
		color: 'icon/primary/default',
	},
	resource_owner_assigned: { icon: 'file', color: 'icon/primary/default' },
	resource_published: { icon: 'file', color: 'icon/primary/default' },
	resource_unpublished: { icon: 'file', color: 'icon/primary/default' },
	resource_tag_added: { icon: 'file', color: 'icon/primary/default' },
	resource_tag_removed: { icon: 'file', color: 'icon/primary/default' },
	// Table / schema
	tables_dropped: { icon: 'table', color: 'icon/primary/default' },
	table_schema_changed: { icon: 'table', color: 'icon/primary/default' },
	// Team
	joined_team: { icon: 'users', color: 'icon/primary/default' },
	api_key_created: { icon: 'key', color: 'icon/primary/default' },
	// Data access request events
	data_access_request_created: { icon: 'lock', color: 'icon/primary/default' },
	data_access_request_approved: { icon: 'lock', color: 'icon/primary/default' },
	data_access_request_approved_admin: {
		icon: 'lock',
		color: 'icon/primary/default',
	},
	data_access_request_rejected: { icon: 'lock', color: 'icon/primary/default' },
	data_access_request_cancelled: {
		icon: 'lock',
		color: 'icon/primary/default',
	},
	data_access_request_expired: {
		icon: 'lock',
		color: 'icon/primary/default',
	},
	data_access_request_expiring_soon: {
		icon: 'lock',
		color: 'icon/warning/default',
	},
	automation_review: { icon: 'bolt', color: 'icon/primary/default' },
};
